import { ChangeDetectorRef, Component, ComponentFactoryResolver, Input,
         OnInit, ViewChild, ViewContainerRef
       } from '@angular/core';
import { MatIconRegistry } from '@angular/material';
import { DomSanitizer } from '@angular/platform-browser';

import { TrivialError } from 'core/error/error';
import { ErrorService } from 'core/error/error.service';
import { Utils } from 'core/utils';
import { AdditionalInfoItemInterface
       } from './additional-info-item-components/additional-info-item-interface'; // tslint:disable-line
import { AdditionalInfoItemConfig } from './collage-designer-config';

export class AdditionalInfoItemsError extends TrivialError
{
  // i18n.error.additionalInfoItemsError.title
  // i18n.error.additionalInfoItemsError.message
  protected _name: string = 'AdditionalInfoItemsError';
}

@Component({
  selector: 'additional-info-items',
  templateUrl: './additional-info-items.component.html',
  styleUrls: [ './additional-info-items.component.scss' ],
})
export class AdditionalInfoItemsComponent implements OnInit
{
  @Input() public additionalInfoItems: Array<AdditionalInfoItemConfig> = [];

  public trackByLabel: Object = Utils.trackByLabel;
  public filteredAdditionalInfoItems: Array<AdditionalInfoItemConfig> = [];
  public visibleComponents: Array<string> = [];

  @ViewChild('spinner') public spinner: any;
  @ViewChild('loadedComponent', {read: ViewContainerRef})
      public loadedComponent: ViewContainerRef;
  @ViewChild('componentsContainer', {read: ViewContainerRef})
      public componentsContainer: ViewContainerRef;

  constructor(
      private cdr: ChangeDetectorRef,
      private mir: MatIconRegistry,
      private componentFactoryResolver: ComponentFactoryResolver,
      private sanitizer: DomSanitizer,
      private errorService: ErrorService)
  {
    this.mir.addSvgIcon(
        'close',
        this.sanitizer.bypassSecurityTrustResourceUrl(
            'assets/img/icons/close.svg'));
  }

  public async ngOnInit(): Promise<void>
  {
    for (const additionalInfoItem of this.additionalInfoItems) {
      const componentPathName = this.getComponentPathName(additionalInfoItem);
      const component = await this.loadComponent(
          componentPathName,
          additionalInfoItem.label,
          this.componentsContainer);
      if (!await component.isApplyable()) {
        continue;
      }
      this.filteredAdditionalInfoItems.push(additionalInfoItem);
    }

    for (const additionalInfoItem of this.filteredAdditionalInfoItems) {
      if (additionalInfoItem.autoDisplay && !Utils.isSmallDevice()) {
        this.loadContent(additionalInfoItem);
        additionalInfoItem.autoDisplay = false;
        this.visibleComponents.push(additionalInfoItem.label);
      }
    }
  }

  private getComponentPathName(
      additionalInfoItem: AdditionalInfoItemConfig): string|undefined
  {
    const component_match = additionalInfoItem.location.match(
        /.*productdesigner:(.+)/);
    if (component_match && component_match.length === 2) {
      return component_match[1];
    }
    else {
      return 'external-html-content';
    }
  }

  public async onTriggerClicked(label: string): Promise<void>
  {
    const additionalInfoItem = Utils.find(
        this.additionalInfoItems, item => item.label === label);
    if (!additionalInfoItem.location) {
      return;
    }

    if (this.visibleComponents.includes(label)) {
      return;
    }

    this.visibleComponents.push(label);
    if (Utils.isSmallDevice()) {
      Utils.scrollToPosition(0, 0);
    }
    this.cdr.detectChanges();
    await this.loadContent(additionalInfoItem);
  }

  private async loadContent(
      additionalInfoItem: AdditionalInfoItemConfig): Promise<void>
  {
    const componentPathName = this.getComponentPathName(additionalInfoItem);
    try {
      const component = await this.loadComponent(
          componentPathName,
          additionalInfoItem.label,
          this.loadedComponent);
      await component.initComponent(additionalInfoItem);
    }
    catch (error) {
      this.errorService.add(error, 'AdditionalInfoItemsError');
      this.errorService.flush('AdditionalInfoItemsError');
      this.removeComponent(additionalInfoItem.label);
    }
  }

  private async loadComponent(
      componentPathName: string,
      label: string,
      container: ViewContainerRef
      ): Promise<AdditionalInfoItemInterface>
  {
    const componentObject: any = await import(
        `./additional-info-item-components/${componentPathName}.component`);
    const componentName = Object.getOwnPropertyNames(componentObject)[1];

    const factory = this.componentFactoryResolver.resolveComponentFactory(
        componentObject[componentName]);

    const component = <AdditionalInfoItemInterface>container
      .createComponent(factory)
      .instance;
    component.removeComponent = () => this.removeComponent(label);
    return component;
  }

  public removeComponent(label: string): void
  {
    this.visibleComponents.splice(this.visibleComponents.indexOf(label), 1);
    this.loadedComponent.clear();
  }
}



// WEBPACK FOOTER //
// ./src/app/collage-designer/additional-info-items.component.ts